import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close, MonitorHeart, SmartToy, Person } from '@mui/icons-material';
import { TrainingSample, BodyZoneId, ViewType, LLMAnalysis } from '../../types';
import { AnatomySelector } from '../AnatomySelector';

interface TrainingSampleInferenceDialogProps {
  open: boolean;
  sample: TrainingSample | null;
  imageUrl?: string;
  onClose: () => void;
}

interface PredictionItem {
  prediction: string;
  confidence: number;
}

interface PredictResponse {
  prediction: string;
  confidence: number;
  ddx: PredictionItem[];
}

const INFERENCE_BASE_URL = 'http://localhost:2334';

export const TrainingSampleInferenceDialog: React.FC<TrainingSampleInferenceDialogProps> = ({
  open,
  sample,
  imageUrl,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [predictResult, setPredictResult] = useState<PredictResponse | null>(null);
  const [overlayUrl, setOverlayUrl] = useState<string | null>(null);
  const lastRunIdRef = useRef(0);

  // Memoize parsed LLM analysis
  const llmAnalysis = React.useMemo<LLMAnalysis | null>(() => {
    if (!sample?.llm_analysis) return null;
    if (typeof sample.llm_analysis === 'object') return sample.llm_analysis as LLMAnalysis;
    try {
      let parsed = JSON.parse(sample.llm_analysis);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return parsed as LLMAnalysis;
    } catch (e) {
      return null;
    }
  }, [sample?.llm_analysis]);

  // Memoize body zones
  const selectedBodyZones = React.useMemo(() => {
    return new Set<BodyZoneId>((sample?.body_zone_ids ?? []) as BodyZoneId[]);
  }, [sample?.body_zone_ids]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setPredictResult(null);
      setLoading(false);
      if (overlayUrl) {
        URL.revokeObjectURL(overlayUrl);
      }
      setOverlayUrl(null);
      return;
    }
  }, [open, overlayUrl]);

  const buildFormData = (resolvedImageUrl: string, resolvedSample: TrainingSample | null, opacity: number | null) => {
    const form = new FormData();
    form.append('image_url', resolvedImageUrl);

    const text = resolvedSample?.symptoms_description?.trim();
    if (text) {
      form.append('text', text);
    }

    const bodyZoneIds = resolvedSample?.body_zone_ids || [];
    if (bodyZoneIds.length > 0) {
      bodyZoneIds.forEach((z) => form.append('bodyZoneIds', String(z)));
    }

    if (opacity !== null) {
      form.append('overlay_alpha', String(opacity));
    }

    return form;
  };

  const runInference = React.useCallback(async () => {
    if (!imageUrl) {
      setError('No image URL available for this sample.');
      return;
    }

    const runId = Date.now();
    lastRunIdRef.current = runId;

    setLoading(true);
    setError(null);
    setPredictResult(null);

    if (overlayUrl) {
      URL.revokeObjectURL(overlayUrl);
      setOverlayUrl(null);
    }

    const body = buildFormData(imageUrl, sample, null);
    try {
      const predictRes = await fetch(`${INFERENCE_BASE_URL}/predict`, {
        method: 'POST',
        body,
      });
      if (!predictRes.ok) {
        const message = await predictRes.text();
        throw new Error(message || `Predict failed: ${predictRes.status} ${predictRes.statusText}`);
      }
      const predictJson = (await predictRes.json()) as PredictResponse;

      if (lastRunIdRef.current === runId) {
        setPredictResult(predictJson);
      }

      const visRes = await fetch(`${INFERENCE_BASE_URL}/visualize`, {
        method: 'POST',
        body: buildFormData(imageUrl, sample, 0.1),
      });
      if (!visRes.ok) {
        const message = await visRes.text();
        throw new Error(message || `Visualize failed: ${visRes.status} ${visRes.statusText}`);
      }
      const visBlob = await visRes.blob();

      if (lastRunIdRef.current === runId) {
        const url = URL.createObjectURL(visBlob);
        setOverlayUrl(url);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Inference failed';
      if (lastRunIdRef.current === runId) {
        setError(message);
      }
    } finally {
      if (lastRunIdRef.current === runId) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, sample]);

  useEffect(() => {
    if (!open || !sample) {
      return;
    }

    void runInference();
  }, [open, sample, runInference]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '85vh', maxHeight: 800 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" component="div" fontWeight="bold">Local Inference Analysis</Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {!sample ? (
          <Box p={3}><Alert severity="warning">No sample selected.</Alert></Box>
        ) : (
          <Grid container sx={{ height: '100%' }}>
            {/* Left Column: Inference Visuals & Results */}
            <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', p: 2, overflowY: 'auto', maxHeight: '100%' }}>
              <Stack spacing={2}>
                {/* Image / Overlay Area */}
                <Box>
                  {overlayUrl ? (
                    <Box sx={{ position: 'relative', width: '100%', minHeight: 240, bgcolor: 'black', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                      <Box
                        component="img"
                        src={overlayUrl}
                        alt="Attention overlay"
                        sx={{ width: '100%', height: '100%', maxHeight: 320, objectFit: 'contain', display: 'block', mx: 'auto' }}
                      />
                      <Chip 
                        label="Attention Map" 
                        size="small" 
                        color="primary" 
                        sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', height: 24 }} 
                      />
                    </Box>
                  ) : imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt="Training sample"
                      sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                    />
                  ) : null}
                </Box>

                {/* Status / Loading */}
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Running inference on {INFERENCE_BASE_URL}...
                    </Typography>
                  </Box>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                {/* Results Card */}
                {predictResult && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Top Diagnosis</Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100', borderRadius: 2, mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                          {predictResult.prediction}
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                          {predictResult.confidence.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 8, bgcolor: 'white', borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(100, Math.max(0, predictResult.confidence))}%`, height: '100%', bgcolor: 'primary.main', transition: 'width 1s ease-out' }} />
                      </Box>
                    </Paper>

                    <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                      Differential Diagnosis (DDx)
                    </Typography>
                    <Stack spacing={1}>
                      {predictResult.ddx.slice(0, 5).map((item, index) => (
                        <Box key={index}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold', color: 'text.secondary' }}>
                                {index + 1}
                              </Box>
                              <Typography variant="body2" fontWeight={500}>{item.prediction}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium">{item.confidence.toFixed(1)}%</Typography>
                          </Box>
                          <Box sx={{ width: 'calc(100% - 28px)', ml: 'auto', height: 4, bgcolor: 'grey.100', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ 
                              width: `${Math.min(100, Math.max(0, item.confidence))}%`, 
                              height: '100%', 
                              bgcolor: index === 0 ? 'primary.main' : 'grey.400',
                              opacity: index === 0 ? 1 : 0.6 
                            }} />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Right Column: Clinical Context */}
            <Grid item xs={12} md={6} sx={{ p: 2, bgcolor: 'grey.50', overflowY: 'auto', maxHeight: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>Clinical Context</Typography>
              <Stack spacing={2}>
                {/* Original Diagnosis */}
                <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MonitorHeart color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="bold">Original ML Diagnosis</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">{sample.prediction || 'N/A'}</Typography>
                    {sample.confidence && (
                      <Chip label={`${(sample.confidence * 100).toFixed(1)}%`} size="small" variant="outlined" sx={{ height: 24 }} />
                    )}
                  </Box>
                </Paper>

                {/* LLM Analysis */}
                {llmAnalysis && (
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SmartToy color="action" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight="bold">LLM Analysis</Typography>
                    </Box>
                    <Typography variant="body2" paragraph fontWeight="medium" sx={{ mb: 1 }}>
                      {llmAnalysis.diagnosis || 'No specific diagnosis'}
                    </Typography>
                    {llmAnalysis.why && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', lineHeight: 1.4 }}>
                        "{llmAnalysis.why}"
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Patient History */}
                <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="bold">Patient History</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                    {sample.symptoms_description || 'No description provided.'}
                  </Typography>
                </Paper>

                {/* Anatomy Visualization */}
                {selectedBodyZones.size > 0 && (
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>Affected Areas</Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', height: 300, overflow: 'hidden' }}>
                      <Box sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, bgcolor: 'background.paper', px: 0.5, borderRadius: 0.5 }}>Front</Typography>
                        <Box sx={{width: '50%' }}>
                           <AnatomySelector
                             viewType={ViewType.front}
                             selectedZones={selectedBodyZones}
                             onZoneSelected={() => {}}
                           />
                        </Box>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, bgcolor: 'background.paper', px: 0.5, borderRadius: 0.5 }}>Back</Typography>
                         <Box sx={{ width: '50%' }}>
                           <AnatomySelector
                             viewType={ViewType.back}
                             selectedZones={selectedBodyZones}
                             onZoneSelected={() => {}}
                           />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};
